import React, { useState } from "react";
import axios from "axios";
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBBtn,
  MDBInput,
  MDBTextArea,
  MDBCheckbox,
} from "mdb-react-ui-kit";
import "./AddProductWizard.css";

// Import marketplace logos
import ebayLogo from "../../assets/marketplaces/ebay.svg";
import shopifyLogo from "../../assets/marketplaces/shopify.svg";
import depopLogo from "../../assets/marketplaces/depop.svg";
import vintedLogo from "../../assets/marketplaces/vinted.svg";
import poshmarkLogo from "../../assets/marketplaces/poshmark.svg";

const AddProductWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1 - Images
    uploadedImages: [], // array
    imageProcessing: false,
    backgroundRemoval: false,
    blurDetection: false,
    upscaleImage: false,

    // Step 2 - Listing Details
    title: "",
    description: "",
    sku: "",
    price: "",
    pricerange: "",
    quantity: "1",
    brand: "",
    color: "",
    category: "",
    condition: "",
    weight: "",
    tags: "",

    // Variants
    variants: [{ id: 1, size: "", color: "", sku: "" }],

    // Step 3 - Marketplaces
    selectedMarketplaces: [],
  });

  const steps = [
    { id: 1, title: "Images" },
    { id: 2, title: "Listing Details" },
    { id: 3, title: "Marketplaces" },
    { id: 4, title: "Review" },
  ];

  // const categories = [
  //   "Electronics",
  //   "Fashion",
  //   "Home & Garden",
  //   "Sports & Outdoors",
  //   "Toys & Games",
  //   "Beauty & Health",
  //   "Books",
  //   "Automotive",
  //   "Jewelry & Accessories",
  //   "Music & Instruments",
  // ];

  const conditions = [
    { value: "new", label: "New" },
    { value: "like-new", label: "Like New" },
    { value: "excellent", label: "Excellent" },
    { value: "good", label: "Good" },
    { value: "fair", label: "Fair" },
    { value: "poor", label: "Poor" },
  ];

  const marketplaces = [
    { id: "ebay", name: "eBay", logo: ebayLogo },
    { id: "shopify", name: "Shopify", logo: shopifyLogo },
    { id: "poshmark", name: "Poshmark", logo: poshmarkLogo },
    { id: "depop", name: "Depop", logo: depopLogo },
    { id: "vinted", name: "Vinted", logo: vintedLogo },
  ];
  const availableMarketplaces = [
    { id: "ebay", name: "eBay", logo: ebayLogo },
    { id: "shopify", name: "Shopify", logo: shopifyLogo },
    { id: "poshmark", name: "Poshmark", logo: poshmarkLogo },
  ];

  const upcomingMarketplaces = [
    { id: "depop", name: "Depop", logo: depopLogo },
    { id: "vinted", name: "Vinted", logo: vintedLogo },
  ];

  // File upload handler
  // const handleFileUpload = (event) => {
  //   const file = event.target.files;
  //   if (file) {
  //     // Validate file type
  //     const validTypes = ["image/jpeg", "image/png", "image/webp"];
  //     if (!validTypes.includes(file.type)) {
  //       alert("Please upload JPG, PNG, or WebP images only.");
  //       return;
  //     }

  //     // Validate file size (5MB max)
  //     const maxSize = 5 * 1024 * 1024;
  //     if (file.size > maxSize) {
  //       alert("Image size must be less than 5MB.");
  //       return;
  //     }

  //     // Show processing state
  //     setFormData((prev) => ({
  //       ...prev,
  //       imageProcessing: true,
  //       uploadedImage: null,
  //     }));

  //     // Simulate processing delay with skeleton loader
  //     setTimeout(() => {
  //       const reader = new FileReader();
  //       reader.onload = (e) => {
  //         setFormData((prev) => ({
  //           ...prev,
  //           uploadedImage: e.target.result,
  //           imageProcessing: false,
  //         }));
  //       };
  //       reader.readAsDataURL(file);
  //     }, 1500);
  //   }
  // };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    const maxSize = 5 * 1024 * 1024;

    const validFiles = files.filter((file) => {
      if (!validTypes.includes(file.type)) {
        alert(`${file.name} is not a valid image.`);
        return false;
      }
      if (file.size > maxSize) {
        alert(`${file.name} exceeds 5MB.`);
        return false;
      }
      return true;
    });

    if (!validFiles.length) return;

    setFormData((prev) => ({ ...prev, imageProcessing: true }));

    const readers = validFiles.map(
      (file) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(file);
        })
    );

    Promise.all(readers).then((images) => {
      setFormData((prev) => ({
        ...prev,
        uploadedImages: [...prev.uploadedImages, ...images],
        imageProcessing: false,
      }));
    });
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // const handleDragDrop = (e) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   const files = e.dataTransfer.files;
  //   if (files.length > 0) {
  //     const fakeEvent = { target: { files: [files] } };
  //     handleFileUpload(fakeEvent);
  //   }
  // };

  const handleDragDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const fakeEvent = { target: { files: files } };
      handleFileUpload(fakeEvent);
    }
  };

  // Form input handlers
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleVariantChange = (variantId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      variants: prev.variants.map((variant) =>
        variant.id === variantId ? { ...variant, [field]: value } : variant
      ),
    }));
  };

  const addVariant = () => {
    const newId = Math.max(...formData.variants.map((v) => v.id)) + 1;
    setFormData((prev) => ({
      ...prev,
      variants: [...prev.variants, { id: newId, size: "", color: "", sku: "" }],
    }));
  };

  const removeVariant = (variantId) => {
    if (formData.variants.length > 1) {
      setFormData((prev) => ({
        ...prev,
        variants: prev.variants.filter((variant) => variant.id !== variantId),
      }));
    }
  };

  const handleMarketplaceToggle = (marketplaceId) => {
    setFormData((prev) => ({
      ...prev,
      selectedMarketplaces: prev.selectedMarketplaces.includes(marketplaceId)
        ? prev.selectedMarketplaces.filter((id) => id !== marketplaceId)
        : [...prev.selectedMarketplaces, marketplaceId],
    }));
  };

  // Navigation
  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Step navigation via stepper click
  const goToStep = (stepId) => {
    // Can only go to previous steps or current step, not future steps
    if (stepId <= getMaxAccessibleStep()) {
      setCurrentStep(stepId);
    }
  };

  // Determine max accessible step based on validation
  // const getMaxAccessibleStep = () => {
  //   // Step 1 is always accessible
  //   let maxStep = 1;

  //   // Step 2 accessible if step 1 is valid
  //   if (formData.uploadedImage !== null) {
  //     maxStep = 2;
  //   }

  //   // Step 3 accessible if step 2 is valid
  //   if (
  //     formData.uploadedImage !== null &&
  //     formData.title &&
  //     formData.description &&
  //     formData.sku &&
  //     formData.price &&
  //     formData.quantity
  //   ) {
  //     maxStep = 3;
  //   }

  //   // Step 4 accessible if step 3 is valid
  //   if (
  //     formData.uploadedImage !== null &&
  //     formData.title &&
  //     formData.description &&
  //     formData.sku &&
  //     formData.price &&
  //     formData.quantity &&
  //     formData.selectedMarketplaces.length > 0
  //   ) {
  //     maxStep = 4;
  //   }

  //   return maxStep;
  // };
  const getMaxAccessibleStep = () => {
    let maxStep = 1;

    // Step 2 accessible if at least one image uploaded
    if (formData.uploadedImages.length > 0) maxStep = 2;

    // Step 3 accessible if step 2 required fields filled
    if (
      formData.uploadedImages.length > 0 &&
      formData.title &&
      formData.description &&
      formData.sku &&
      formData.price &&
      formData.quantity
    ) {
      maxStep = 3;
    }

    // Step 4 accessible if step 3 selected marketplaces
    if (
      formData.uploadedImages.length > 0 &&
      formData.title &&
      formData.description &&
      formData.sku &&
      formData.price &&
      formData.quantity &&
      formData.selectedMarketplaces.length > 0
    ) {
      maxStep = 4;
    }

    return maxStep;
  };

  // Check if step is clickable
  const isStepClickable = (stepId) => {
    return stepId <= getMaxAccessibleStep();
  };

  // Validation
  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.uploadedImages.length > 0;
      case 2:
        return (
          formData.title &&
          formData.description &&
          formData.sku &&
          formData.price &&
          formData.quantity
        );
      case 3:
        return formData.selectedMarketplaces.length > 0;
      case 4:
        return true;
      default:
        return false;
    }
  };

  //   const isStepValid = () => {
  //   switch (currentStep) {
  //     case 1:
  //       return formData.uploadedImages.length > 0;
  //     case 2:
  //       return formData.title && formData.description && formData.sku && formData.price && formData.quantity;
  //     case 3:
  //       return formData.selectedMarketplaces.length > 0;
  //     case 4:
  //       return true;
  //     default:
  //       return false;
  //   }
  // };

  // Image controls
  // const removeImage = () => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     uploadedImage: null,
  //     imageProcessing: false,
  //   }));
  // };

  // const replaceImage = () => {
  //   document.getElementById("file-input").click();
  // };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      uploadedImages: prev.uploadedImages.filter((_, i) => i !== index),
    }));
  };

  const replaceImage = (index) => {
    const input = document.getElementById("file-input");
    input.onchange = (e) => {
      const file = e.target.files;
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        setFormData((prev) => {
          const newImages = [...prev.uploadedImages];
          newImages[index] = ev.target.result;
          return { ...prev, uploadedImages: newImages };
        });
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  // // inside AddProductWizard component
  // const handleAutoFill = async () => {
  //   if (!formData.uploadedImage) {
  //     alert("Please upload an image first!");
  //     return;
  //   } else {
  //     console.log(formData.uploadedImage);
  //   }

  //   try {
  //     const form = new FormData();

  //     // Convert base64 image to Blob
  //     const res = await fetch(formData.uploadedImage);
  //     const blob = await res.blob();
  //     form.append("images", blob, "product.png");

  //     const response = await axios.post(
  //       "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/autofill/analyze",
  //       form,
  //       {
  //         headers: { "Content-Type": "multipart/form-data" },
  //       }
  //     );

  //     // Update form fields with API response
  //     const data = response.data;
  //     // Populate form fields
  //     setFormData((prev) => ({
  //       ...prev,
  //       title: data.title || prev.title,
  //       description: data.description || prev.description,
  //       sku: data.sku || prev.sku,
  //       price: data.price || prev.price, // optional, you can use recommended from response.price.recommended
  //       brand: data.brand || prev.brand,
  //       category: data.category || prev.category,
  //       condition: data.condition || prev.condition,
  //     }));

  //     console.log("✅ AI Response:", data);
  //   } catch (error) {
  //     console.error("❌ Auto-Fill Error:", error);
  //     alert("Failed to auto-fill listing. Check console.");
  //   }
  // };

  const handleAutoFill = async () => {
    if (!formData.uploadedImages.length) {
      alert("Please upload at least one image first!");
      return;
    }

    try {
      const form = new FormData();

      // Saari images ko Blob me convert karke append karna
      formData.uploadedImages.forEach((base64Image, index) => {
        // base64 to Blob
        const res = fetch(base64Image);
        res
          .then((r) => r.blob())
          .then((blob) => {
            form.append("images", blob, `product-${index + 1}.png`);

            // Agar last image ho to request bhej do
            if (index === formData.uploadedImages.length - 1) {
              sendAutoFillRequest(form);
            }
          });
      });
    } catch (error) {
      console.error("❌ Auto-Fill Error:", error);
      alert("Failed to auto-fill listing. Check console.");
    }
  };

  // Separate function for API call
  const sendAutoFillRequest = async (form) => {
    try {
      const response = await axios.post(
        "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/autofill/analyze",
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const data = response.data;

      // Map condition (example same logic)
      let conditionValue = "";
      const analysis = data.analysis || {};
      const metadata = data.product_metadata || {};
      const priceData = data.price || {};

      if (analysis.condition >= 10) conditionValue = "new";
      else if (analysis.condition >= 9) conditionValue = "like-new";
      else if (analysis.condition >= 8) conditionValue = "excellent";
      else if (analysis.condition >= 7) conditionValue = "good";
      else if (analysis.condition >= 6) conditionValue = "fair";
      else conditionValue = "poor";

      setFormData((prev) => ({
        ...prev,
        title: metadata.title || prev.title,
        description: metadata.description || prev.description,
        sku: metadata.sku || prev.sku,
        price: priceData.recommended || prev.price,
        pricerange:
          priceData.low && priceData.high
            ? `${priceData.low} - ${priceData.high}`
            : prev.pricerange,
        brand: analysis.brand || "Others",
        category: metadata.category || prev.category,
        condition: conditionValue,
        color: analysis.color || prev.color,
      }));

      console.log("✅ Auto-fill done for multiple images", data);
    } catch (error) {
      console.error("❌ Auto-Fill API Error:", error);
      alert("Failed to auto-fill listing. Check console.");
    }
  };

  // Dummy api caalll
  // const handleAutoFill = async () => {
  //   if (!formData.uploadedImages.length) {
  //     alert("Please upload at least one image first!");
  //     return;
  //   }

  //   const firstImage = formData.uploadedImages[0];

  //   // Dummy response data (replace with your API call)
  //   const data = {
  //     analysis: {
  //       brand: "",
  //       category: "Topwear",
  //       color: "White",
  //       condition: 10,
  //       gender: "Unisex",
  //       material: "",
  //       subcategory: "T-shirt",
  //     },
  //     price: {
  //       high: 138,
  //       low: 102,
  //       recommended: 120,
  //     },
  //     product_metadata: {
  //       category: "Topwear",
  //       description:
  //         "Check out these t-shirt topwear by  (Unisex). Color: white, Material: , Condition: 10/10. Perfect for collectors or daily wear.",
  //       gender: "Unisex",
  //       sku: "TOP-507",
  //       subcategory: "T-shirt",
  //       title: "Topwear (T-shirt, Unisex) in White (), Condition 10/10",
  //     },
  //   };

  //   const metadata = data.product_metadata;
  //   const analysis = data.analysis;
  //   const priceData = data.price;

  //   // Map condition (7-10) to select value
  //   let conditionValue = "";
  //   if (analysis.condition >= 10) conditionValue = "new";
  //   else if (analysis.condition >= 9) conditionValue = "like-new";
  //   else if (analysis.condition >= 8) conditionValue = "excellent";
  //   else if (analysis.condition >= 7) conditionValue = "good";
  //   else if (analysis.condition >= 6) conditionValue = "fair";
  //   else conditionValue = "poor";

  //   setFormData((prev) => ({
  //     ...prev,
  //     title: metadata.title || prev.title,
  //     description: metadata.description || prev.description,
  //     sku: metadata.sku || prev.sku,
  //     price: priceData.recommended || prev.price,
  //     pricerange: `${priceData.low} - ${priceData.high}`,
  //     brand: analysis.brand || "Others",
  //     category: metadata.category || prev.category,
  //     condition: conditionValue,
  //     color: analysis.color || prev.color,
  //   }));

  //   console.log(
  //     "✅ Auto-fill done for multiple images, used first image:",
  //     firstImage
  //   );
  // };

  const handleSubmit = () => {
    alert("Product listing created successfully! (Demo)");
  };

  const handleExportCSV = () => {
    if (!formData) return;

    // CSV headers
    const headers = [
      "Marketplace",
      "Title",
      "Description",
      "SKU",
      "Price",
      "Quantity",
      "Brand",
      "Category",
      "Condition",
      "Color",
      "Variants",
    ];

    // Prepare rows
    const rows = [];

    // Main listing (general)
    rows.push([
      "All Marketplaces",
      formData.title,
      formData.description,
      formData.sku,
      formData.price,
      formData.quantity,
      formData.brand,
      formData.category,
      formData.condition,
      formData.color,
      formData.variants
        .map((v) => `Size: ${v.size}, Color: ${v.color}, SKU: ${v.sku}`)
        .join(" | "),
    ]);

    // Marketplace-specific
    formData.selectedMarketplaces.forEach((id) => {
      let marketplaceName = marketplaces.find((m) => m.id === id)?.name || id;

      let row = [
        marketplaceName,
        formData.title,
        formData.description,
        formData.sku,
        formData.price,
        formData.quantity,
        formData.brand,
        formData.category,
        formData.condition,
        formData.color,
        formData.variants
          .map((v) => `Size: ${v.size}, Color: ${v.color}, SKU: ${v.sku}`)
          .join(" | "),
      ];

      rows.push(row);
    });

    // Convert to CSV string
    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.map((v) => `"${v}"`).join(",")),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "product_listing.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <MDBContainer fluid className="add-product-wizard">
      <div className="wizard-container">
        {/* Header */}
        <div className="wizard-header">
          <h1 className="wizard-title">Add Product</h1>
          <p className="wizard-subtitle">
            Create a new product listing in 4 simple steps
          </p>
        </div>

        {/* Stepper */}
        <div className="stepper">
          {steps.map((step, index) => (
            <div key={step.id} className="step-container">
              <div
                className={`step ${currentStep >= step.id ? "active" : ""} ${
                  currentStep === step.id ? "current" : ""
                } ${isStepClickable(step.id) ? "clickable" : "disabled"}`}
              >
                <div
                  className="step-number"
                  onClick={() => goToStep(step.id)}
                  style={{
                    cursor: isStepClickable(step.id) ? "pointer" : "default",
                  }}
                >
                  {currentStep > step.id ? "✓" : step.id}
                </div>
                <div className="step-label">{step.title}</div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`step-connector ${
                    currentStep > step.id ? "completed" : ""
                  }`}
                ></div>
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="step-content">
          {/* STEP 1: IMAGES */}
          {currentStep === 1 && (
            <div className="step-panel">
              <div className="step-header">
                <h2>Upload Product Images</h2>
                <p>Add high-quality images of your product</p>
              </div>

              {/* Upload Area */}
              {/* <div
                className="upload-area"
                onDragOver={handleDragOver}
                onDrop={handleDragDrop}
                onClick={() =>
                  !formData.uploadedImage &&
                  !formData.imageProcessing &&
                  document.getElementById("file-input").click()
                }
              >
                <input
                  id="file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                />

                {formData.imageProcessing ? (
                  <div className="skeleton-loader">
                    <div className="skeleton-shimmer"></div>
                  </div>
                ) : formData.uploadedImage ? (
                  // <div className="image-preview">
                  //   <img src={formData.uploadedImage} alt="Product preview" />
                  //   <div className="image-controls">
                  //     <MDBBtn size="sm" color="light" onClick={replaceImage}>
                  //       Replace
                  //     </MDBBtn>
                  //     <MDBBtn
                  //       size="sm"
                  //       className="btn-red-outline"
                  //       onClick={removeImage}
                  //     >
                  //       Remove
                  //     </MDBBtn>
                  //   </div>
                  // </div>
                  <div className="image-preview-container">
                    {formData.uploadedImages.length > 0 ? (
                      formData.uploadedImages.map((img, index) => (
                        <div key={index} className="image-preview">
                          <img src={img} alt={`Product ${index + 1}`} />
                          <div className="image-controls">
                            <MDBBtn
                              size="sm"
                              color="light"
                              onClick={() => replaceImage(index)}
                            >
                              Replace
                            </MDBBtn>
                            <MDBBtn
                              size="sm"
                              className="btn-red-outline"
                              onClick={() => removeImage(index)}
                            >
                              Remove
                            </MDBBtn>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="upload-placeholder">
                        <div className="upload-icon">📷</div>
                        <h3>Drop Images OR Click to Upload</h3>
                        <p>JPG, PNG, WebP • Max 5MB</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <div className="upload-icon">📷</div>
                    <h3>Drop Images OR Click to Upload</h3>
                    <p>JPG, PNG, WebP • Max 5MB</p>
                  </div>
                )}
              </div> */}
              <div
                className="upload-area"
                onDragOver={handleDragOver}
                onDrop={handleDragDrop}
                onClick={() =>
                  !formData.imageProcessing &&
                  document.getElementById("file-input").click()
                }
              >
                <input
                  id="file-input"
                  type="file"
                  accept="image/*"
                  capture="environment" // opens camera on mobile
                  multiple
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                />

                {formData.imageProcessing ? (
                  <div className="skeleton-loader">
                    <div className="skeleton-shimmer"></div>
                  </div>
                ) : formData.uploadedImages.length > 0 ? (
                  <div className="image-preview-container">
                    {formData.uploadedImages.map((img, index) => (
                      <div key={index} className="image-preview">
                        <img src={img} alt={`Product ${index + 1}`} />
                        <div className="image-controls">
                          <MDBBtn
                            size="sm"
                            color="light"
                            onClick={() => replaceImage(index)}
                          >
                            Replace
                          </MDBBtn>
                          <MDBBtn
                            size="sm"
                            className="btn-red-outline"
                            onClick={() => removeImage(index)}
                          >
                            Remove
                          </MDBBtn>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <div className="upload-icon">📷</div>
                    <h3>Drop Images OR Click to Upload</h3>
                    <p>JPG, PNG, WebP • Max 5MB</p>
                  </div>
                )}
              </div>

              {/* Image Options */}
              <div className="image-options">
                <h4>Enhancement Options</h4>
                <p className="options-subtitle">
                  These enhancements will be applied before listing.
                </p>

                <div className="option-checkboxes">
                  <MDBCheckbox
                    id="background-removal"
                    label="Background Removal"
                    checked={formData.backgroundRemoval}
                    onChange={(e) =>
                      handleInputChange("backgroundRemoval", e.target.checked)
                    }
                  />
                  <MDBCheckbox
                    id="blur-detection"
                    label="Blur Detection / Remove Blur"
                    checked={formData.blurDetection}
                    onChange={(e) =>
                      handleInputChange("blurDetection", e.target.checked)
                    }
                  />
                  <MDBCheckbox
                    id="upscale-image"
                    label="Upscale Image"
                    checked={formData.upscaleImage}
                    onChange={(e) =>
                      handleInputChange("upscaleImage", e.target.checked)
                    }
                  />
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: LISTING DETAILS */}
          {currentStep === 2 && (
            <div className="step-panel">
              <div className="step-header">
                <h2>Listing Details</h2>
                <p>Add comprehensive product information</p>
                <MDBBtn
                  color="light"
                  size="sm"
                  className="auto-fill-btn"
                  onClick={handleAutoFill}
                >
                  Auto-Fill Listing
                </MDBBtn>
              </div>

              <MDBRow>
                {/* Required Fields Section */}
                <MDBCol md="12" className="mb-3">
                  <h4 className="section-title">Required Information</h4>
                </MDBCol>

                <MDBCol md="12">
                  <MDBInput
                    label="Product Title *"
                    type="text"
                    size="lg"
                    className="mb-3"
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    required
                  />
                </MDBCol>

                <MDBCol md="12">
                  <MDBTextArea
                    label="Product Description *"
                    rows="4"
                    className="mb-3"
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    required
                  />
                </MDBCol>

                <MDBCol md="4">
                  <MDBInput
                    label="SKU *"
                    type="text"
                    size="lg"
                    className="mb-3"
                    value={formData.sku}
                    onChange={(e) => handleInputChange("sku", e.target.value)}
                    required
                  />
                </MDBCol>

                <MDBCol md="4">
                  <MDBInput
                    label="Price *"
                    type="number"
                    size="lg"
                    className="mb-3"
                    value={formData.price}
                    onChange={(e) => handleInputChange("price", e.target.value)}
                    required
                    step="0.01"
                    min="0"
                  />
                </MDBCol>
                <MDBCol md="4">
                  <MDBInput
                    label="Price Range"
                    type="text"
                    size="lg"
                    className="mb-3"
                    value={formData.pricerange}
                    onChange={(e) =>
                      handleInputChange("pricerange", e.target.value)
                    }
                  />
                </MDBCol>

                <MDBCol md="4">
                  <MDBInput
                    label="Quantity *"
                    type="number"
                    size="lg"
                    className="mb-3"
                    value={formData.quantity}
                    onChange={(e) =>
                      handleInputChange("quantity", e.target.value)
                    }
                    required
                    min="1"
                  />
                </MDBCol>

                {/* Additional Details Section */}
                <MDBCol md="12" className="mb-3 mt-4">
                  <h4 className="section-title">Additional Details</h4>
                </MDBCol>

                <MDBCol md="6">
                  <MDBInput
                    label="Brand"
                    type="text"
                    size="lg"
                    className="mb-3"
                    value={formData.brand}
                    onChange={(e) => handleInputChange("brand", e.target.value)}
                  />
                </MDBCol>
                <MDBCol md="6">
                  <MDBInput
                    label="Color"
                    type="text"
                    size="lg"
                    className="mb-3"
                    value={formData.color}
                    onChange={(e) => handleInputChange("color", e.target.value)}
                  />
                </MDBCol>

                <MDBCol md="6">
                  <MDBInput
                    label="Category"
                    type="text"
                    size="lg"
                    className="mb-3"
                    value={formData.category}
                    onChange={(e) =>
                      handleInputChange("category", e.target.value)
                    }
                  />
                  {/* <select
                    className="form-select form-select-lg mb-3"
                    value={formData.category}
                    onChange={(e) =>
                      handleInputChange("category", e.target.value)
                    }
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select> */}
                </MDBCol>

                <MDBCol md="4">
                  <select
                    className="form-select form-select-lg mb-3"
                    value={formData.condition}
                    onChange={(e) =>
                      handleInputChange("condition", e.target.value)
                    }
                  >
                    <option value="">Select Condition</option>
                    {conditions.map((condition) => (
                      <option key={condition.value} value={condition.value}>
                        {condition.label}
                      </option>
                    ))}
                  </select>
                </MDBCol>

                <MDBCol md="4">
                  <MDBInput
                    label="Weight (lbs)"
                    type="number"
                    size="lg"
                    className="mb-3"
                    value={formData.weight}
                    onChange={(e) =>
                      handleInputChange("weight", e.target.value)
                    }
                    step="0.1"
                    min="0"
                  />
                </MDBCol>

                {/* <MDBCol md="4">
                  <MDBInput
                    label="Dimensions (L x W x H)"
                    type="text"
                    size="lg"
                    className="mb-3"
                    value={formData.dimensions}
                    onChange={(e) =>
                      handleInputChange("dimensions", e.target.value)
                    }
                    placeholder="e.g., 10 x 8 x 2 inches"
                  />
                </MDBCol> */}

                <MDBCol md="12">
                  <MDBInput
                    label="Tags / Keywords"
                    type="text"
                    size="lg"
                    className="mb-3"
                    value={formData.tags}
                    onChange={(e) => handleInputChange("tags", e.target.value)}
                    placeholder="Comma separated tags"
                  />
                </MDBCol>
              </MDBRow>

              {/* Product Variants */}
              <div className="variants-section">
                <div className="variants-header">
                  <h4 className="section-title">Product Variants</h4>
                  <MDBBtn
                    size="sm"
                    className="btn-red-outline"
                    onClick={addVariant}
                  >
                    + Add Variant
                  </MDBBtn>
                </div>

                {formData.variants.map((variant, index) => (
                  <div key={variant.id} className="variant-card">
                    <div className="variant-header">
                      <h5>Variant {index + 1}</h5>
                      {formData.variants.length > 1 && (
                        <MDBBtn
                          size="sm"
                          color="light"
                          onClick={() => removeVariant(variant.id)}
                        >
                          Remove
                        </MDBBtn>
                      )}
                    </div>

                    <MDBRow>
                      <MDBCol md="4">
                        <MDBInput
                          label="Size"
                          type="text"
                          className="mb-3"
                          value={variant.size}
                          onChange={(e) =>
                            handleVariantChange(
                              variant.id,
                              "size",
                              e.target.value
                            )
                          }
                        />
                      </MDBCol>
                      <MDBCol md="4">
                        <MDBInput
                          label="Color"
                          type="text"
                          className="mb-3"
                          value={variant.color}
                          onChange={(e) =>
                            handleVariantChange(
                              variant.id,
                              "color",
                              e.target.value
                            )
                          }
                        />
                      </MDBCol>
                      <MDBCol md="4">
                        <MDBInput
                          label="Variant SKU"
                          type="text"
                          className="mb-3"
                          value={variant.sku}
                          onChange={(e) =>
                            handleVariantChange(
                              variant.id,
                              "sku",
                              e.target.value
                            )
                          }
                        />
                      </MDBCol>
                    </MDBRow>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 3: MARKETPLACES */}
          {currentStep === 3 && (
            <div className="step-panel">
              <div className="step-header">
                <h2>Select Marketplaces</h2>
                <p>Choose where you want to list your product</p>
              </div>

              {/* <div className="marketplace-grid">
                {marketplaces.map((marketplace) => (
                  <div
                    key={marketplace.id}
                    className={`marketplace-card ${
                      formData.selectedMarketplaces.includes(marketplace.id)
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => handleMarketplaceToggle(marketplace.id)}
                  >
                    <div className="marketplace-logo">
                      <img
                        src={marketplace.logo}
                        alt={`${marketplace.name} logo`}
                      />
                    </div>
                    <h4>{marketplace.name}</h4>
                    <div className="marketplace-check">
                      {formData.selectedMarketplaces.includes(marketplace.id)
                        ? "✓"
                        : ""}
                    </div>
                  </div>
                ))}
              </div> */}
              <div className="marketplace-grid">
                {availableMarketplaces.map((marketplace) => (
                  <div
                    key={marketplace.id}
                    className={`marketplace-card ${
                      formData.selectedMarketplaces.includes(marketplace.id)
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => handleMarketplaceToggle(marketplace.id)}
                  >
                    <div className="marketplace-logo">
                      <img
                        src={marketplace.logo}
                        alt={`${marketplace.name} logo`}
                      />
                    </div>
                    <h4>{marketplace.name}</h4>
                    <div className="marketplace-check">
                      {formData.selectedMarketplaces.includes(marketplace.id)
                        ? "✓"
                        : ""}
                    </div>
                  </div>
                ))}

                {/* Upcoming marketplaces */}
                {upcomingMarketplaces.map((marketplace) => (
                  <div
                    key={marketplace.id}
                    className="marketplace-card upcoming"
                  >
                    <div className="marketplace-logo">
                      <img
                        src={marketplace.logo}
                        alt={`${marketplace.name} logo`}
                      />
                    </div>
                    <h4>{marketplace.name}</h4>
                    <div className="marketplace-badge">Coming Soon</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 4: REVIEW */}
          {currentStep === 4 && (
            <div className="step-panel">
              <div className="step-header">
                <h2>Review & Submit</h2>
                <p>Please review your product details before submitting</p>
              </div>

              <div className="review-sections">
                {/* Image Review */}
                {/* <div className="review-section">
                  <h4>Product Image</h4>
                  {formData.uploadedImage && (
                    <div className="review-image">
                      <img
                        src={formData.uploadedImage}
                        alt="Product preview"
                      />
                    </div>
                  )}
                </div> */}
                <div className="review-section">
                  <h4>Product Images</h4>
                  <div className="review-image">
                    {formData.uploadedImages.map((img, index) => (
                      <img key={index} src={img} alt={`Product ${index + 1}`} />
                    ))}
                  </div>
                </div>

                {/* Product Details Review */}
                <div className="review-section">
                  <h4>Product Details</h4>
                  <div className="review-grid">
                    <div className="review-item">
                      <strong>Title:</strong> {formData.title}
                    </div>
                    <div className="review-item">
                      <strong>Description:</strong> {formData.description}
                    </div>
                    <div className="review-item">
                      <strong>SKU:</strong> {formData.sku}
                    </div>
                    <div className="review-item">
                      <strong>Price:</strong> ${formData.price}
                    </div>
                    <div className="review-item">
                      <strong>Quantity:</strong> {formData.quantity}
                    </div>
                    {formData.brand && (
                      <div className="review-item">
                        <strong>Brand:</strong> {formData.brand}
                      </div>
                    )}
                    {formData.category && (
                      <div className="review-item">
                        <strong>Category:</strong> {formData.category}
                      </div>
                    )}
                  </div>
                </div>

                {/* Variants Review */}
                {formData.variants.some((v) => v.size || v.color || v.sku) && (
                  <div className="review-section">
                    <h4>Product Variants</h4>
                    <div className="variants-review">
                      {formData.variants.map((variant, index) => (
                        <div key={variant.id} className="variant-review-item">
                          <strong>Variant {index + 1}:</strong>
                          {variant.size && <span>Size: {variant.size}</span>}
                          {variant.color && <span>Color: {variant.color}</span>}
                          {variant.sku && <span>SKU: {variant.sku}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Marketplaces Review */}
                <div className="review-section">
                  <h4>Selected Marketplaces</h4>
                  <div className="selected-marketplaces">
                    {formData.selectedMarketplaces.map((id) => {
                      const marketplace = marketplaces.find((m) => m.id === id);
                      if (!marketplace) return null;

                      return (
                        <div key={id} className="marketplace-preview">
                          <span className="marketplace-badge">
                            {marketplace.name}
                          </span>

                          {/* Marketplace-specific product details */}
                          <div className="marketplace-details">
                            {id === "shopify" && (
                              <div className="review-grid">
                                <div className="review-item">
                                  <strong>Title:</strong> {formData.title}
                                </div>
                                <div className="review-item">
                                  <strong>Description:</strong>{" "}
                                  {formData.description}
                                </div>
                                <div className="review-item">
                                  <strong>Price:</strong> ${formData.price}
                                </div>
                                <div className="review-item">
                                  <strong>SKU:</strong> {formData.sku}
                                </div>
                              </div>
                            )}

                            {id === "ebay" && (
                              <div className="review-grid">
                                <div className="review-item">
                                  <strong>Title:</strong> {formData.title}
                                </div>
                                <div className="review-item">
                                  <strong>SKU:</strong> {formData.sku}
                                </div>
                                <div className="review-item">
                                  <strong>Price:</strong> ${formData.price}
                                </div>
                              </div>
                            )}

                            {id === "poshmark" && (
                              <div className="review-grid">
                                <div className="review-item">
                                  <strong>Title:</strong> {formData.title}
                                </div>
                                <div className="review-item">
                                  <strong>Price:</strong> ${formData.price}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="wizard-navigation">
          <MDBBtn
            color="light"
            disabled={currentStep === 1}
            onClick={prevStep}
            className="nav-btn"
          >
            Back
          </MDBBtn>

          {currentStep < steps.length ? (
            <MDBBtn
              className="btn-red"
              disabled={!isStepValid()}
              onClick={nextStep}
            >
              Next
            </MDBBtn>
          ) : (
            <>
              <MDBBtn className="btn-red" onClick={handleSubmit}>
                Create Listing
              </MDBBtn>

              {/* Export CSV Button */}
              <MDBBtn
                className="btn-outline-dark ms-3"
                onClick={handleExportCSV}
              >
                Export CSV
              </MDBBtn>
            </>
          )}
        </div>
      </div>
    </MDBContainer>
  );
};

export default AddProductWizard;
